import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import axios from 'axios';
import { Link, usePage } from '@inertiajs/react';

const CreditsInfo = forwardRef(({ movie_title, movie_hash, initialCredits, user_email }, ref) => {
    const [credits, setCredits] = useState(initialCredits);
    // VAT-inclusive pack labels + disclosure, built server-side (HandleInertiaRequests 'vat', plan v4):
    // the browser never does its own tax maths for these. Defaults are today's net prices.
    const vat = usePage().props.vat || {};
    const pack100 = (vat.packs && vat.packs[100]) || '$10.00';
    const pack30 = (vat.packs && vat.packs[30]) || '$5.00';
    // Net + VAT behind each pack price, '' where prices are net (2026-10-02).
    const vat100 = (vat.pack_breakdowns && vat.pack_breakdowns[100]) || '';
    const vat30 = (vat.pack_breakdowns && vat.pack_breakdowns[30]) || '';
    // Credit top-ups post to a Stripe Checkout Session (checkout.credit_pack) so Stripe automatic
    // tax applies. Replaced the legacy checkout.js → charge_brainstorm.php buttons on 2026-09-28
    // (a bare Stripe Charge cannot carry tax), along with the /api/stripe-session call they made.
    const csrfToken = typeof document !== 'undefined'
        ? document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        : '';
    const returnTo = typeof window !== 'undefined' ? window.location.href : '';

    // Expose the refresh function to parent components
    useImperativeHandle(ref, () => ({
        refreshCredits: async () => {
            console.log('Refreshing credits for movie:', movie_hash);
            try {
                const response = await axios.get(`/credits/${movie_hash}`);
                setCredits(response.data.credits);
                console.log('Updated credits:', response.data.credits);
                return response.data.credits;
            } catch (error) {
                console.error('Error fetching credits:', error);
                throw error;
            }
        }
    }));

    // Update local state when initialCredits prop changes
    useEffect(() => {
        setCredits(initialCredits);
    }, [initialCredits]);


    return (
        <div className="my-2">
            <div className="d-flex justify-content-between align-items-center">
                <a target='_blank' href={`/view_analysis?movieid=${movie_hash}`} className="btn primary_btn ml-4">
                {movie_title} Analysis <i className="fa fa-external-link"></i>
                </a>
                <div className="d-flex justify-content-end align-items-center">
                    <span className="mr-3 px-4">
                        <b>Credits</b>: <span style={{ backgroundColor: 'blue', fontSize: '1.3em' }} className="badge text-light">{credits}</span>
                    </span>
                    <button className="btn primary_btn" data-bs-toggle="modal" data-bs-target="#creditsModal">Buy Credits</button>
                </div>
            </div>

            <div className="modal fade" id="creditsModal" tabIndex="-1" aria-labelledby="creditsModalLabel" aria-hidden="true">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header text-white bg_primary">
                            <h5 className="modal-title">Replenish Credits</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <div className="alert alert-warning" role="alert">
                                <h5>Warning - this will refresh the page and discard any unsaved edits you have made.</h5>
                            </div>

                            <div className="shadow-lg border border-2 border-dark rounded-3 px-sm-5 px-4 py-4 my-5 mx-auto text-center">
                                <center>
                                    <form action="/checkout/credits" method="POST">
                                        <h5 className="fw-bold">Purchase a pack of 100 credits</h5>
                                        {vat100 && <p className="small text-muted mb-2">({vat100})</p>}
                                        <input type="hidden" name="_token" value={csrfToken} />
                                        <input type="hidden" name="pack" value="100" />
                                        <input type="hidden" name="return_to" value={returnTo} />
                                        <button type="submit" className="btn primary_btn px-4">Buy Now - {pack100}</button>
                                    </form>
                                </center>
                            </div>

                            <div className="shadow-lg border border-2 border-dark rounded-3 px-sm-5 px-4 py-4 my-5 mx-auto text-center">
                                <center>
                                    <form action="/checkout/credits" method="POST">
                                        <h5 className="fw-bold">Purchase a pack of 30 credits</h5>
                                        {vat30 && <p className="small text-muted mb-2">({vat30})</p>}
                                        <input type="hidden" name="_token" value={csrfToken} />
                                        <input type="hidden" name="pack" value="30" />
                                        <input type="hidden" name="return_to" value={returnTo} />
                                        <button type="submit" className="btn primary_btn px-4">Buy Now - {pack30}</button>
                                    </form>
                                </center>
                            </div>

                            {vat.note && (
                                <p className="text-center" style={{ fontSize: '.85rem', color: '#5b6b76' }}>{vat.note}</p>
                            )}

                            <center>
                                <h4>Or</h4>
                            </center>
                            <div className="shadow-lg border border-2 border-dark rounded-3 px-sm-5 px-4 py-4 my-5 mx-auto text-center">
                                <h5>Got a voucher?</h5>
                                <h3>Voucher Code</h3>
                                <form id="improve_scene_form" action="/voucher_improve.php" method="post" encType="multipart/form-data">
                                    <input type="hidden" name="movieid" value={movie_hash} />
                                    <div className="row">
                                        <div className="col-md-6 mb-3 mx-auto">
                                            <input type="text" className="form-control" name="code" id="code" />
                                        </div>
                                    </div>
                                    <button type="submit" className="btn secondary_btn px-4 py-2 fw-semibold">Load me up!</button>
                                </form>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default CreditsInfo;